import type { FmSection } from "@/components/ui/sections/section.types";
import type { FmDeviceTier } from "@/components/ui/FmResponsive";
import type { FmRowActionConfirm } from "@/components/ui/FmRowActions";
import type { FmRightInspectorDescriptor } from "@/components/ui/FmRightInspector";
import type { FmFormFlowPageDescriptor } from "@/components/ui/FmFormFlowPage.types";
import type { FmConfirmationCopyDescriptor } from "@/components/ui/FmConfirm";
import type { FmAnalysisWorkspaceDescriptor } from "@/components/ui/FmAnalysisWorkspaceRoot.types";
import type { FmOperationalWorkspaceDescriptor } from "@/components/ui/FmOperationalWorkspaceRoot.types";
import type {
  FmPrimaryMapWorkspaceData,
  WorkspacePrimaryMapSlotKey,
} from "@/shared/config/mapSlotRegistry";

export type FmMapWorkspaceMode =
  "find" | "orient" | "inspect" | "risk" | "refine";
export const FM_MAP_RISK_ACCESS_ACTION_ID = "open-risk-access" as const;
export const FM_MAP_PROPERTY_SEARCH_ACTION_ID = "open-property-search" as const;
export const FM_MAP_EVIDENCE_PACKAGE_ACTION_ID = "export-evidence-package" as const;
export const MAP_WORKSPACE_STATES = [
  "ready",
  "loading",
  "empty",
  "partial",
  "stale",
  "locked",
  "error",
] as const;
export type FmMapWorkspaceState = (typeof MAP_WORKSPACE_STATES)[number];

export type FmMapWorkspaceStoryBeat = "mode-answer" | "map";

export type FmMapWorkspaceStateBehavior = Readonly<
  Record<
    FmMapWorkspaceState,
    {
      answer: string;
      retainsServedAnswer: boolean;
    }
  >
>;

/**
 * The page-neutral story contract for one workspace mode. The mode owns one
 * question and answer while the registered map remains the dominant beat.
 */
export interface FmMapWorkspaceStoryDescriptor {
  ownerQuestion: string;
  literalAnswer: string;
  dominantBeat: "map";
  requiredServedFields: readonly string[];
  requiredProvenance: readonly string[];
  allowedTransitions: readonly FmMapWorkspaceMode[];
  stateBehavior: FmMapWorkspaceStateBehavior;
  mobileOrder: readonly FmMapWorkspaceStoryBeat[];
  conversionRole: "find" | "orient" | "inspect" | "assess-risk" | "refine";
}

interface SelectionBase {
  id: string;
  label: string;
  /** Null keeps a real collection entity selectable when no served geometry exists. */
  geometryRef: string | null;
}

export type FmMapSelection =
  | { kind: "none" }
  | ({ kind: "property"; propertyId: string } & SelectionBase)
  | ({ kind: "stand"; propertyId: string; standId: string } & SelectionBase)
  | ({
      kind: "campaign";
      campaignId: string;
      campaignIds: readonly string[];
      propertyIds: readonly string[];
      plotIds: readonly string[];
    } & SelectionBase)
  | ({
      kind: "plot";
      propertyId: string;
      campaignId: string;
      plotId: string;
      standId: string | null;
    } & SelectionBase)
  | ({
      kind: "risk";
      propertyId: string;
      riskId: string;
      // Canonical HazardSeverity ramp minus `none` (never selected) — see
      // ActiveHazardSeverity in api/propertyHazardReport.ts. Kept as a literal
      // union here (not an import) so this DS archetype file stays free of a
      // hazard-domain dependency; the two vocabularies are kept in sync by
      // buildMapWorkspaceDescriptor.ts assigning real pin.severity values.
      severity: "elevated" | "high" | "severe";
    } & SelectionBase);

export interface FmMapViewportRef {
  id: string;
  revision: string;
  frame: "all" | "property" | "selection" | "risk";
}

export type FmMapLayerId =
  | "property-boundary"
  | "forest-stands"
  | "measurement-plots"
  | "sentry-risks"
  | "rain-radar";

interface FmMapLayerBase {
  id: FmMapLayerId;
  sourceId: string;
  label: string;
  visible: boolean;
}

export type FmMapLayerDescriptor =
  | (FmMapLayerBase & { id: "property-boundary"; kind: "boundary" })
  | (FmMapLayerBase & { id: "forest-stands"; kind: "stands" })
  | (FmMapLayerBase & { id: "measurement-plots"; kind: "measurement-plots" })
  | (FmMapLayerBase & {
      id: "sentry-risks";
      kind: "risk";
      access:
        | { kind: "available"; advisory: string }
        | { kind: "loading"; label: string }
        | { kind: "error"; message: string }
        | { kind: "unavailable"; reason: string }
        | {
            kind: "not-evaluable";
            reason: string;
            unwatchedStandCount: number;
          }
        | {
            kind: "locked";
            title: string;
            explanation: string;
            actionId: typeof FM_MAP_RISK_ACCESS_ACTION_ID;
            actionLabel: string;
          };
    })
  | (FmMapLayerBase & { id: "rain-radar"; kind: "radar" });

export interface FmMapRadarFrameDescriptor {
  id: string;
  capturedAt: string;
  grid: {
    crs: string;
    bbox: readonly [number, number, number, number];
  };
  render:
    | { status: "ready"; imageUrl: string }
    | { status: "unavailable"; reason: string };
}

export interface FmMapRadarDescriptor {
  propertyId: string | null;
  window?: { start: string | null; end: string | null } | null;
  state:
    "empty" | "loading" | "ready" | "partial" | "stale" | "locked" | "error";
  frames: readonly FmMapRadarFrameDescriptor[];
  affectedStandCount: FmAvailability<number>;
  maximumIntensity: FmAvailability<{ value: number; unit: "mm/h" }>;
  /**
   * Accumulated rainfall over the served window (mm) — property-level when
   * served, else the worst covered stand. Distinct signal from
   * `maximumIntensity` (instantaneous mm/h peak): this is the cumulative
   * total, the basis for a rainfall-DEFICIT read (AE1) as opposed to a
   * single storm-intensity spike.
   */
  accumulatedRain: FmAvailability<{ value: number; unit: "mm" }>;
  noDataReason: string | null;
  error: string | null;
  summaryError: string | null;
  reload: () => void;
  copy: {
    title: string;
    ariaLabel: string;
    controlsAriaLabel: string;
    reloadAria: string;
    previousAria: string;
    playAria: string;
    pauseAria: string;
    nextAria: string;
    opacityLabel: string;
    timeRangeLabel: string;
    selectProperty: string;
    loadingTimeline: string;
    unavailable: string;
    noFrames: string;
    frameUnavailable: string;
    affectedStands: string;
    affectedSummary: string;
    affectedSummaryWithRain: string;
    noStandOverlap: string;
    loaded: string;
    noTimestamp: string;
  };
}

export interface FmMapBasemapDescriptor {
  id: "streets" | "satellite" | "terrain";
  label: string;
}

export interface FmMapFeatureDescriptor {
  id: string;
  sourceId: string;
  geometryRef: string | null;
  label: string;
  kind: "property" | "stand" | "campaign" | "plot" | "risk";
  propertyId?: string;
  campaignId?: string;
  plotId?: string;
  standId?: string | null;
  relatedPlotIds?: readonly string[];
  relatedCampaignIds?: readonly string[];
  relatedPropertyIds?: readonly string[];
  mappingStatus?: "auto" | "auto_review" | "manual" | "unmapped";
  description?: string;
}

export interface FmMapFilterOption {
  value: string;
  label: string;
}

export interface FmMapFilterDescriptor {
  id: string;
  sourceId: string;
  label: string;
  operator: "one-of" | "boolean";
  options: readonly FmMapFilterOption[];
  selectedValues: readonly string[];
}

/** Complete semantic filter snapshot emitted by the workspace renderer. */
export type FmMapFilterValues = Readonly<Record<string, readonly string[]>>;

export interface FmMapActionDescriptor {
  id: string;
  label: string;
  intent: "primary" | "secondary" | "inspect" | "destructive";
  /** Opts a page-level action into the archetype-owned compact map toolbar. */
  tool?: "create" | "refresh" | "search";
  /** Close the originating compact tool panel before opening a larger context surface. */
  dismissesPanel?: boolean;
  disabledReason?: string;
  confirm?: FmRowActionConfirm;
}

export interface FmMapCollectionRecordDescriptor {
  id: string;
  title: string;
  description?: string;
  status?: string;
  metadata: readonly { id: string; label: string }[];
  featureId?: string;
  actions: readonly FmMapActionDescriptor[];
}

interface PanelBase {
  id: string;
  title: string;
}

export interface FmMapLegendSemantics {
  baseMapLabel: string;
  fitMapLabel: string;
  zoomInLabel: string;
  zoomOutLabel: string;
  legendLabel: string;
  species: readonly { code: string; label: string }[];
  speciesLabel: string;
  speciesExplanation: string;
  volumeLabel: string;
  volumeExplanation: string;
  maturityLabel: string;
  maturityValueLabel: string;
  maturityExplanation: string;
  visibleLayerLabel: string;
  hiddenLayerLabel: string;
  featureLegend?: {
    title: string;
    entries: readonly {
      status: NonNullable<FmMapFeatureDescriptor["mappingStatus"]>;
      label: string;
    }[];
  };
}

export type FmMapPanelDescriptor =
  | (PanelBase & {
      kind: "legend";
      layerIds: readonly FmMapLayerId[];
      semantics: FmMapLegendSemantics;
    })
  | (PanelBase & {
      kind: "filters";
      filterIds: readonly string[];
      resetLabel: string;
    })
  | (PanelBase & {
      kind: "collection";
      records: readonly FmMapCollectionRecordDescriptor[];
      selectedIds: readonly string[];
      emptyLabel: string;
      actionLabel: string;
      overflowLabel: string;
      actions: readonly FmMapActionDescriptor[];
      primaryAction?: FmMapActionDescriptor;
      /** Keep the collection open when a row is selected so list and map stay paired. */
      retainOnSelect?: boolean;
    })
  /** Shared section content (the workspace's source evidence, for example). */
  | (PanelBase & { kind: "section"; section: FmSection });

export interface FmMapFormFlowDialogDescriptor {
  kind: "form-flow";
  id: string;
  open: boolean;
  title: string;
  closeLabel: string;
  closeConfirmation?: FmConfirmationCopyDescriptor;
  size: "md" | "lg";
  flow: FmFormFlowPageDescriptor;
}

export interface FmMapOperationalDetailDialogDescriptor {
  kind: "operational-detail";
  id: string;
  open: boolean;
  title: string;
  closeLabel: string;
  size: "lg";
  workspace: FmOperationalWorkspaceDescriptor;
}

export interface FmMapAnalysisDetailDialogDescriptor {
  kind: "analysis-detail";
  id: string;
  open: boolean;
  title: string;
  closeLabel: string;
  size: "lg";
  workspace: FmAnalysisWorkspaceDescriptor;
}

export type FmMapWorkspaceDialogDescriptor =
  | FmMapFormFlowDialogDescriptor
  | FmMapAnalysisDetailDialogDescriptor
  | FmMapOperationalDetailDialogDescriptor;

export type FmAvailability<T> =
  { status: "available"; value: T } | { status: "unavailable"; reason: string };

export interface FmMapOrientationDescriptor {
  locale?: "en" | "fi" | "sv" | "es";
  ariaLabel: string;
  propertyLabel: string;
  area: FmAvailability<{ value: number; unit: "ha" }>;
  standCount: FmAvailability<number>;
  matureStandCount: FmAvailability<number>;
  dominantSpecies: FmAvailability<string>;
  totalVolume: FmAvailability<{ value: number; unit: "m3" }>;
  volumeRange: FmAvailability<{ low: number; high: number; unit: "m3" }>;
  /** Formatted date the served inventory was observed. */
  observedOn: FmAvailability<string>;
  /** Formatted date the source grew the inventory to by model; may lie in the future. */
  growthCalculatedTo: FmAvailability<string>;
  source: FmAvailability<string>;
}

export type FmMapWorkspaceEvent =
  | { kind: "panel-change"; panelId: string | null }
  | { kind: "selection-change"; selection: FmMapSelection }
  | {
      kind: "filter-change";
      filterId: string;
      values: readonly string[];
      activeFilters: FmMapFilterValues;
      source: "panel" | "undo";
    }
  | { kind: "layer-change"; layerId: FmMapLayerId; visible: boolean }
  | { kind: "basemap-change"; baseMapId: FmMapBasemapDescriptor["id"] }
  | { kind: "inspection-close" }
  | { kind: "inspection-open-full" }
  | { kind: "collection-select"; panelId: string; recordId: string }
  | { kind: "dialog-dismiss"; dialogId: string }
  | { kind: "action"; actionId: string };

export type FmMapCollectionSelectEvent = Extract<
  FmMapWorkspaceEvent,
  { kind: "collection-select" }
>;

export interface FmMapWorkspaceNoticeDescriptor {
  title: string;
  description: string;
  actionId: string;
  actionLabel: string;
  testId: string;
}

export interface FmMapWorkspaceDescriptor {
  id: string;
  propertyId: string | null;
  mode: FmMapWorkspaceMode;
  modeLabel: string;
  story: FmMapWorkspaceStoryDescriptor;
  mapSlotKey: WorkspacePrimaryMapSlotKey;
  mapData: FmPrimaryMapWorkspaceData | null;
  viewport: FmMapViewportRef;
  geometryRefs: readonly string[];
  baseMaps: readonly FmMapBasemapDescriptor[];
  activeBaseMapId: FmMapBasemapDescriptor["id"];
  layers: readonly FmMapLayerDescriptor[];
  radar: FmMapRadarDescriptor;
  features: readonly FmMapFeatureDescriptor[];
  selection: FmMapSelection;
  inspection: FmRightInspectorDescriptor | null;
  source: { id: string; label: string };
  filters: readonly FmMapFilterDescriptor[];
  actions: readonly FmMapActionDescriptor[];
  panels: readonly FmMapPanelDescriptor[];
  dialogs?: readonly FmMapWorkspaceDialogDescriptor[];
  activePanelId: string | null;
  state: {
    kind: FmMapWorkspaceState;
    label: string;
    recoveryActionId?: string;
  };
  copy: {
    rendererUnavailable: string;
    appliedFiltersAriaLabel: string;
  };
  orientation?: FmMapOrientationDescriptor;
  /**
   * Typed return context from another owner surface. The renderer owns the
   * banner; routes pass served copy and an action id, never a callback.
   */
  notice?: FmMapWorkspaceNoticeDescriptor;
  onEvent?: (event: FmMapWorkspaceEvent) => void;
}

export interface FmMapWorkspaceRootProps {
  descriptor: FmMapWorkspaceDescriptor;
  /** Enables the deterministic registered preview renderer without fixing a tier. */
  preview?: boolean;
  /** Deterministic gallery/test tier; production routes omit it. */
  previewTier?: FmDeviceTier;
  "data-testid"?: string;
}
